import classNames from 'classnames';
import React, { useRef, useState, useEffect } from 'react';
import styles from './style.module.scss';
import { PlusIcon, ProjectNameIcon, StatusCheckIcon, EditIcon, DelIcon, DownloadIcon } from 'assets';
import { useSelector } from 'react-redux';
import * as XLSX from 'xlsx';
import GenericTable from 'shared/components/common/table/table';
import Heading from 'shared/components/common/heading/Heading';
import CustomButton from 'shared/components/common/custom_button/CustomButton';
import DashboardWrapper from 'shared/components/common/dashboard_wrapper/DashboardWrapper';
import AddMatchProduct from 'shared/modal/add_match_product/AddMatchProduct';
import ModalImage from 'shared/components/common/custom_image_lightbox/ModalImage';
import { 
    GetAllMatchProductsQuery, 
    DeleteMatchProductMutation, 
    BulkAddMatchProductsMutation 
} from 'shared/services/match_product_service/MatchProductService';
import OverlayLoading from 'shared/screen_loader/overlay_loader/OverlayLoading';
import DeleteConfirmation from 'shared/modal/delete_confirmation/DeleteConfirmation';
import SearchBar from 'shared/components/common/searchbar/SearchBar';
import { toastMessage } from 'shared/components/toast/Toast';

export default function MatchProducts() {
    const { theme: { theme } } = useSelector((state) => state.root);
    
    // Mutations & Queries
    const { mutate: deleteMutate, isLoading: deleteLoading, isSuccess: deleteSuccess } = DeleteMatchProductMutation();
    const { mutate: bulkAddMutate, isLoading: bulkAddLoading } = BulkAddMatchProductsMutation();
    
    const [searchMenu, setsearchMenu] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    
    const { isLoading: queryLoading, data: listData } = GetAllMatchProductsQuery();

    // Modal control states
    const [openAddModal, setOpenAddModal] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [openDelConfirm, setOpenDelConfirm] = useState(false);
    
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (listData) {
            setSearchResults(listData);
        }
    }, [listData]);

    useEffect(() => {
        if (deleteSuccess) {
            setSelectedItem(null);
            setOpenDelConfirm(false);
        }
    }, [deleteSuccess]);

    const handleExcelUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const bstr = evt.target.result;
                const wb = XLSX.read(bstr, { type: 'binary' });
                const wsname = wb.SheetNames[0];
                const ws = wb.Sheets[wsname];
                const parsedData = XLSX.utils.sheet_to_json(ws);

                const mappedRecords = [];
                for (let i = 0; i < parsedData.length; i++) {
                    const row = parsedData[i];
                    const prodName = row["Product Name"] || row["Models"] || row["Model"];
                    const groupName = row["Group"];
                    const heightVal = row["Height"];
                    const widthVal = row["Width"];
                    // Images could be comma separated strings or empty in excel
                    const imagesStr = row["Images"] || row["Image"];

                    if (prodName) {
                        let parsedImages = [];
                        if (imagesStr && typeof imagesStr === 'string') {
                            parsedImages = imagesStr.split(',').map(s => s.trim()).filter(s => s);
                        }

                        mappedRecords.push({
                            product_name: prodName.toString().trim(),
                            group: groupName ? groupName.toString().trim() : "",
                            height: heightVal ? heightVal.toString().trim() : "",
                            width: widthVal ? widthVal.toString().trim() : "",
                            images: parsedImages
                        });
                    }
                }

                if (mappedRecords.length === 0) {
                    toastMessage('error', 'No valid records found in the Excel file! Make sure the column is "Models" or "Product Name".');
                    return;
                }

                bulkAddMutate(mappedRecords);
            } catch (err) {
                console.error("Excel import failed", err);
                toastMessage('error', 'Failed to parse Excel file!');
            }
        };
        reader.readAsBinaryString(file);
        e.target.value = null; // reset input
    };

    const downloadSampleExcel = () => {
        const sampleData = [
            {
                "Models": "iPhone 15 Pro Max",
                "Group": "Group 1",
                "Height": "159.90",
                "Width": "76.70",
                "Images": "https://example.com/img1.png, https://example.com/img2.png"
            },
            {
                "Models": "Samsung Galaxy S24 Ultra",
                "Group": "Group 2",
                "Height": "162.30",
                "Width": "79.00",
                "Images": ""
            }
        ];
        const worksheet = XLSX.utils.json_to_sheet(sampleData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Sample Match Products");
        XLSX.writeFile(workbook, "Match_Products_Sample.xlsx");
    };

    const handleImportClick = () => {
        fileInputRef.current?.click();
    };

    const columns = React.useMemo(
        () => [
            {
                Header: (
                    <div className={classNames(`${theme}FontColor w-100`)}>
                        <ProjectNameIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        Models
                    </div>
                ),
                accessor: 'product_name',
                Cell: ({ row }) => {
                    const text = row.original.product_name || '';
                    const parts = text.split(',');
                    return (
                        <div className="w-100">
                            {parts.map((part, index) => (
                                <React.Fragment key={index}>
                                    {part.trim()}
                                    {index < parts.length - 1 && <br />}
                                </React.Fragment>
                            ))}
                        </div>
                    );
                }
            },
            {
                Header: (
                    <div className={classNames(`${theme}FontColor w-100`)}>
                        <ProjectNameIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        Group
                    </div>
                ),
                accessor: 'group',
                Cell: ({ row }) => <div className="w-100">{row.original.group || '-'}</div>
            },
            {
                Header: (
                    <div className={classNames(`${theme}FontColor w-100`)}>
                        <ProjectNameIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        Height
                    </div>
                ),
                accessor: 'height',
                Cell: ({ row }) => <div className="w-100">{row.original.height || '-'}</div>
            },
            {
                Header: (
                    <div className={classNames(`${theme}FontColor w-100`)}>
                        <ProjectNameIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        Width
                    </div>
                ),
                accessor: 'width',
                Cell: ({ row }) => <div className="w-100">{row.original.width || '-'}</div>
            },
            {
                Header: (
                    <div className={classNames(`${theme}FontColor w-100`)}>
                        <ProjectNameIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        Images
                    </div>
                ),
                accessor: 'images',
                Cell: ({ row }) => {
                    const images = row.original.images && row.original.images.length > 0 
                                     ? row.original.images 
                                     : row.original.image ? [row.original.image] : [];
                    return (
                        <div className="d-flex align-items-center">
                            {images.length > 0 ? (
                                <div className="position-relative" style={{ width: '40px', height: '40px', cursor: 'pointer' }}>
                                    <ModalImage 
                                        small={images[0]} 
                                        large={images[0]} 
                                        alt="product" 
                                        className={styles.matchedImage} 
                                        style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '4px' }}
                                        imageArray={images}
                                        currentIndex={0}
                                    />
                                    {images.length > 1 && (
                                        <div 
                                            style={{
                                                position: 'absolute', 
                                                top: '-4px', 
                                                right: '-4px', 
                                                background: '#d9534f', 
                                                color: 'white', 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                justifyContent: 'center',
                                                borderRadius: '50%',
                                                minWidth: '18px',
                                                height: '18px',
                                                padding: '0 4px',
                                                pointerEvents: 'none',
                                                fontSize: '11px',
                                                fontWeight: 'bold',
                                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                                border: '1px solid white',
                                                zIndex: 10
                                            }}>
                                            {images.length}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className={styles.placeholderImg}>No Img</div>
                            )}
                        </div>
                    );
                }
            },
            {
                Header: (
                    <div className={classNames(`${theme}FontColor w-100 text-end me-2`)}>
                        <StatusCheckIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        Action
                    </div>
                ),
                accessor: 'action',
                Cell: ({ row }) => (
                    <div className="d-flex justify-content-end align-items-center gap-2 me-2">
                        <EditIcon
                            className="pointer"
                            style={{ width: '18px', height: '18px' }}
                            onClick={() => {
                                setSelectedItem(row.original);
                                setIsEdit(true);
                                setOpenAddModal(true);
                            }}
                        />
                        <DelIcon
                            className="pointer"
                            onClick={() => {
                                setSelectedItem(row.original);
                                setOpenDelConfirm(true);
                            }}
                        />
                    </div>
                )
            }
        ],
        [theme]
    );

    return (
        <DashboardWrapper>
            <div className={classNames(styles.UserContainer, 'text-start')}>
                <SearchBar
                    searchKey="product_name"
                    suggestionsSource={listData}
                    onSearchChange={setSearchResults}
                />
                <div className={styles.Header}>
                    <Heading heading={'Screen Protector Match Products'} headingStyle={styles.Heading} />
                    <div className="d-flex">
                        <button className={styles.DownloadBtn} onClick={downloadSampleExcel}>
                            <DownloadIcon style={{ marginRight: '6px', width: '16px', height: '16px' }} />
                            Download Template
                        </button>
                        <button className={styles.ImportBtn} onClick={handleImportClick}>
                            Import Excel
                        </button>
                        <CustomButton
                            Icon={PlusIcon}
                            IconStyle={classNames('me-1', styles.IconStyle)}
                            text={'Match Product'}
                            submitHandle={() => {
                                setIsEdit(false);
                                setSelectedItem(null);
                                setOpenAddModal(true);
                            }}
                            containerStyle={classNames(styles.AddProduct)}
                        />
                        <input
                            type="file"
                            ref={fileInputRef}
                            style={{ display: 'none' }}
                            accept=".xlsx, .xls"
                            onChange={handleExcelUpload}
                        />
                    </div>
                </div>
                <GenericTable 
                    columns={columns} 
                    data={searchResults || []} 
                    TDTextStyle={classNames(styles.TDTextStyle)} 
                    TableWrapperStyle={classNames(styles.Table)} 
                />
                <AddMatchProduct 
                    show={openAddModal} 
                    handleClose={() => setOpenAddModal(false)} 
                    isEdit={isEdit} 
                    selectedItem={selectedItem} 
                    datatable={listData}
                />
                <DeleteConfirmation
                    show={openDelConfirm}
                    loading={deleteLoading}
                    ConfimationHeading="Delete Match Product"
                    Desc={'Are you sure you want to delete this Match Product record?'}
                    DeleteHandler={() => {
                        if (selectedItem) deleteMutate(selectedItem._id);
                    }}
                    handleClose={() => setOpenDelConfirm(false)}
                />
                {(queryLoading || bulkAddLoading) && <OverlayLoading />}
            </div>
        </DashboardWrapper>
    );
}
